import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React, { useMemo } from 'react';
import * as styles from './CommentPreview.module.scss';
import DeltaPreview from '../../components/DeltaPreview';
import { MentionBaseProvider } from '../../editor/mentions/MentionBase';
import { isEmojiChar } from '../../utils/emoji';

const mergeNormalLines = (content: Op[]) => {
  let hasMerged = false;
  const lines: { delta: Delta; attributes: Record<string, unknown> }[] = [];
  new Delta(content).eachLine((delta, attributes) => {
    const lastLine = _.last(lines);
    if (
      !lastLine ||
      !_.isEmpty(attributes) ||
      !_.isEmpty(lastLine.attributes)
    ) {
      lines.push({ delta, attributes });
    } else {
      lastLine.delta = lastLine.delta.insert(' ').concat(delta);
      hasMerged = true;
    }
  });
  if (!hasMerged) {
    return content;
  }
  let collapsedDelta = new Delta();
  lines.forEach((line) => {
    collapsedDelta = collapsedDelta.concat(line.delta);
    collapsedDelta.insert('\n', line.attributes);
  });
  return collapsedDelta.ops;
};

const getCharIfContentIsEmoji = (ops: Op[]) => {
  if (ops.length === 1 && typeof ops[0]?.insert === 'string') {
    const content = ops[0].insert.trim();
    if (isEmojiChar(content)) {
      return content;
    }
  }

  return null;
};

type CommentPreviewProps = {
  collapseLines?: boolean;
  content: Delta | Op[];
  link?: boolean;
  showHoverCard?: boolean;
  onResize?: () => void;
  initialHeight?: number;
} & (
  | {
      maxLines: number;
      disableLineClamp?: boolean;
      onCanBeClampedChange?: (canBeClamped: boolean) => void;
    }
  | {
      maxLines?: never;
      disableLineClamp?: never;
      onCanBeClampedChange?: never;
    }
);

const CommentPreview = ({
  collapseLines,
  content,
  ...props
}: CommentPreviewProps) => {
  const { ops } = new Delta(content);
  const collapsedContent = useMemo(
    () => (collapseLines ? mergeNormalLines(ops) : ops),
    [ops, collapseLines],
  );
  const emojiChar = getCharIfContentIsEmoji(ops);
  const className = classNames('comment-preview', {
    [styles.singleEmoji]: emojiChar,
  });

  const inner = emojiChar || (
    <MentionBaseProvider containerType="comment">
      <DeltaPreview
        typography="comment"
        content={collapsedContent}
        {...props}
      />
    </MentionBaseProvider>
  );

  return <span className={className}>{inner}</span>;
};

export default CommentPreview;
